import type { BgpCommunities } from '@ipfabric/utils/settings/types/types';
import {
  type BgpFileInfo,
  bgpFileHeaders,
  isBgpFileHeader,
} from '../dataTransfer/bgpDataTransferTypes';

export type ValidationError = {
  id: string;
  message: string;
};

export type ValidationResult = {
  validData: BgpCommunities[];
  errors: ValidationError[];
};

export type lineHandlerProps = {
  line: string;
  rowIndex: number;
  errors: ValidationError[];
  headers: string[] | null;
  validData: BgpCommunities[];
};

export const parseAndValidateDataFromCSV = (
  bgpFileInfo: BgpFileInfo,
  handleLine: (props: lineHandlerProps) => void,
  emptyHeadersAllowed?: boolean,
): Promise<ValidationResult> =>
  new Promise((resolve) => {
    const csvContent = bgpFileInfo.content;
    const requiredHeaders = bgpFileHeaders;
    const errors: ValidationError[] = [];
    const validData: BgpCommunities[] = [];

    // Parse CSV
    const lines = csvContent.split('\n').map((line) => line.trim());
    const headers = emptyHeadersAllowed
      ? null
      : // Fixed noUncheckedIndexedAccess NIM-19408
        lines[0]!
          .split(',')
          .map((h) => h.trim());

    if (emptyHeadersAllowed) {
      // Check for empty or invalid CSV
      if (!headers || lines.length === 1) {
        errors.push({ id: 'Error', message: 'CSV file is empty or invalid.' });
        resolve({ validData, errors });
        return;
      }

      // Check for missing headers
      const missingHeaders = requiredHeaders.filter(
        (header) => !isBgpFileHeader(header),
      );
      if (missingHeaders.length > 0) {
        errors.push({
          id: 'Line 1',
          message: 'Missing headers.',
        });
        resolve({ validData, errors });
        return;
      }
    }

    // Parse and handle each row
    lines.slice(1).forEach((line, rowIndex) => {
      handleLine?.({
        line,
        rowIndex,
        errors,
        headers,
        validData,
      });
    });

    resolve({ validData, errors });
  });
